// ============================================================================
// Safe Care - Gestion des personnes couvertes (client + famille)
// Module : features/assistance_sociale/pages
// Charte : .tab / .formTitle / .bG / .clientP / .tabElement / InputForm
// ============================================================================

import { useState, useEffect } from 'react';
import { Table, Spin, Button, Form, Space, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import InputForm from '../../../Components/InputT';
import {
  useGetAllSouscriptions,
  useGetPersonnesCouvertes,
  useAddPersonneCouverte,
  useDeletePersonneCouverte,
} from '../hooks';
import { SouscriptionSafeCare, PersonneCouverte } from '../types';

const PersonnesCouvertesPage = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');

  // États boutons (pattern cSAL)
  const [ajoutActve, setAjoutActif] = useState(false);
  const [enregActve, setEnregActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [actuActive, setActActive] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);
  const [consulActive, setConsulActive] = useState(false);

  // Souscription sélectionnée
  const [selectedSouscription, setSelectedSouscription] = useState<SouscriptionSafeCare | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Personne couverte sélectionnée (tableau du bas)
  const [selectedPersonne, setSelectedPersonne] = useState<PersonneCouverte | null>(null);
  const [selectedPersonneKeys, setSelectedPersonneKeys] = useState<React.Key[]>([]);

  // Récupération de idSouscription depuis l'URL
  const idSouscriptionUrl = new URLSearchParams(window.location.search).get('idSouscription');
  const idSouscriptionFromUrl = idSouscriptionUrl ? Number(idSouscriptionUrl) : undefined;

  // Hooks
  const { data: souscriptions, isLoading: loadingSouscriptions } = useGetAllSouscriptions('ACTIVE');
  const { data: personnesRes, isLoading: loadingPersonnes } = useGetPersonnesCouvertes(
    selectedSouscription?.IdSouscription ?? idSouscriptionFromUrl
  );
  const addPersonne = useAddPersonneCouverte();
  const deletePersonne = useDeletePersonneCouverte();

  const personnes: PersonneCouverte[] =
    personnesRes?.StatutCode === 200 ? personnesRes.Result?.Table1 ?? [] : [];

  // Si idSouscription présent dans l'URL, on charge directement les personnes couvertes
  useEffect(() => {
    if (idSouscriptionFromUrl) {
      setActiveTab('2');
    }
  }, [idSouscriptionFromUrl]);

  // Sélection d'une ligne de souscription
  const onSelectSouscription = (newSelectedRowKeys: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const record = (souscriptions ?? [])[newSelectedRowKeys];
    setSelectedSouscription(record);
    setActiveTab('2');
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectSouscription,
  };

  // Sélection d'une ligne de personne couverte → remplit le formulaire
  const onSelectPersonne = (newSelectedRowKeys: any) => {
    setSelectedPersonneKeys(newSelectedRowKeys);
    const record = personnes[newSelectedRowKeys];
    setSelectedPersonne(record);
    if (record) {
      form.setFieldsValue(record);
      setSupActive(false);
      setConsulActive(true);
    }
  };
  const rowSelectionPersonne = {
    selectedPersonneKeys,
    onChange: onSelectPersonne,
  };

  function initialiazeButton() {
    setAjoutActif(false);
    setEnregActve(true);
    setSupActive(true);
    setActActive(true);
    setAnnulActive(true);
    setConsulActive(false);
  }

  // Bouton Ajouter : vide le formulaire et active l'enregistrement
  function handleAjouter() {
    form.resetFields();
    setSelectedPersonne(null);
    setSelectedPersonneKeys([]);
    setAjoutActif(true);
    setEnregActve(false);
    setAnnulActive(false);
    setConsulActive(true);
    setSupActive(true);
    // Auto-remplir IdSouscription et Numcli
    const idSous = selectedSouscription?.IdSouscription ?? idSouscriptionFromUrl;
    form.setFieldValue('IdSouscription', idSous);
    form.setFieldValue('Numcli', selectedSouscription?.Numcli ?? '');
  }

  // Bouton Enregistrer : sauvegarde la personne couverte
  function handleEnregistrer() {
    form
      .validateFields()
      .then((values: any) => {
        SweetAlert.fire({
          title: 'Enregistrement',
          text: 'Voulez-vous enregistrer cette personne couverte ?',
          showCancelButton: true,
          icon: 'question',
          confirmButtonText: 'Oui',
          cancelButtonText: 'Non',
          confirmButtonColor: '#116599',
          cancelButtonColor: 'darkred',
        }).then((res) => {
          if (res.isConfirmed) {
            setLoading(true);
            const personne: PersonneCouverte = {
              ...values,
              UtiSai: user.coduti ?? '',
            };
            addPersonne.mutate(personne, {
              onSuccess: (resp) => {
                setLoading(false);
                if (resp?.StatutCode === 200) {
                  SweetAlert.fire({ title: 'Succès', text: resp.Message, icon: 'success' });
                  form.resetFields();
                  setSelectedPersonne(null);
                  setSelectedPersonneKeys([]);
                  initialiazeButton();
                } else {
                  SweetAlert.fire({
                    title: 'Attention',
                    text: resp?.Message ?? 'Erreur lors de l\'enregistrement',
                    icon: 'warning',
                  });
                }
              },
              onError: (error: any) => {
                setLoading(false);
                SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
              },
            });
          }
        });
      })
      .catch(() => {
        SweetAlert.fire({
          title: 'Veuillez remplir tous les champs obligatoires',
          icon: 'warning',
        });
      });
  }

  // Bouton Supprimer : supprime la personne couverte sélectionnée
  function handleSupprimer() {
    if (!selectedPersonne?.IdPersonneCouverte) {
      SweetAlert.fire({ title: 'Sélectionnez une personne à supprimer', icon: 'warning' });
      return;
    }
    SweetAlert.fire({
      title: 'Suppression',
      text: `Supprimer ${selectedPersonne.NomBeneficiaire ?? ''} ${selectedPersonne.PrenomBeneficiaire ?? ''} ?`,
      showCancelButton: true,
      icon: 'warning',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
      confirmButtonColor: 'darkred',
      cancelButtonColor: '#116599',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        deletePersonne.mutate(selectedPersonne.IdPersonneCouverte!, {
          onSuccess: (resp) => {
            setLoading(false);
            if (resp?.StatutCode === 200) {
              SweetAlert.fire({ title: 'Succès', text: resp.Message, icon: 'success' });
              form.resetFields();
              setSelectedPersonne(null);
              setSelectedPersonneKeys([]);
              initialiazeButton();
            } else {
              SweetAlert.fire({
                title: 'Attention',
                text: resp?.Message ?? 'Erreur lors de la suppression',
                icon: 'warning',
              });
            }
          },
          onError: (error: any) => {
            setLoading(false);
            SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
          },
        });
      }
    });
  }

  // Bouton Actualiser
  function handleActualiser() {
    form.resetFields();
    setSelectedPersonne(null);
    setSelectedPersonneKeys([]);
    initialiazeButton();
  }

  // Bouton Annuler
  function handleAnnuler() {
    form.resetFields();
    setSelectedPersonne(null);
    setSelectedPersonneKeys([]);
    initialiazeButton();
  }

  // Bouton Consulter (sélection d'une souscription)
  function handleConsulter() {
    if (!selectedSouscription && !idSouscriptionFromUrl) {
      SweetAlert.fire({ title: 'Sélectionnez une souscription', icon: 'warning' });
      return;
    }
    setActiveTab('2');
  }

  // ==================== COLONNES ====================
  const columnsSous: ColumnsType<SouscriptionSafeCare> = [
    { title: 'N°', dataIndex: 'IdSouscription', width: 60 },
    { title: 'Client', dataIndex: 'NOMCLI', width: 120 },
    { title: 'Prénom', dataIndex: 'PRECLI', width: 120 },
    { title: 'Produit', dataIndex: 'NomProduit', width: 150 },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100 },
    { title: 'Date effet', dataIndex: 'DateEffet', width: 120 },
    { title: 'Date fin', dataIndex: 'DateFin', width: 120 },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
  ];

  const columnsPersonnes: ColumnsType<PersonneCouverte> = [
    { title: 'N°', dataIndex: 'IdPersonneCouverte', width: 60 },
    { title: 'Nom', dataIndex: 'NomBeneficiaire', width: 150 },
    { title: 'Prénom', dataIndex: 'PrenomBeneficiaire', width: 150 },
    { title: 'Date naissance', dataIndex: 'DateNais', width: 120 },
    { title: 'Lien parenté', dataIndex: 'LienParente', width: 120 },
    { title: 'N° Relation', dataIndex: 'NumRel', width: 120 },
  ];

  // ==================== ONGLETS ====================
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Souscriptions actives',
      children: (
        <Box fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 5 }}>
          <Spin spinning={loadingSouscriptions || loading}>
            <Table
              size="small"
              style={{ width: '100%' }}
              rowKey="IdSouscription"
              rowSelection={{ ...rowSelection, type: 'radio' }}
              columns={columnsSous}
              dataSource={souscriptions ?? []}
              scroll={{ y: 250, x: 10 }}
              pagination={{ defaultPageSize: 10, position: ['bottomRight'] }}
            />
          </Spin>
        </Box>
      ),
    },
    {
      key: '2',
      label: 'Personnes couvertes',
      children: (
        <Box fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 5 }}>
          {!selectedSouscription && !idSouscriptionFromUrl ? (
            <Typography color="textSecondary">
              Sélectionnez une souscription dans l'onglet "Souscriptions actives".
            </Typography>
          ) : (
            <Spin spinning={loadingPersonnes || loading}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Personnes couvertes
                {selectedSouscription
                  ? ` - ${selectedSouscription.NOMCLI} ${selectedSouscription.PRECLI ?? ''} (${selectedSouscription.NomProduit ?? ''})`
                  : ''}
              </Typography>
              <Table
                size="small"
                style={{ width: '100%' }}
                rowKey="IdPersonneCouverte"
                rowSelection={{ ...rowSelectionPersonne, type: 'radio' }}
                columns={columnsPersonnes}
                dataSource={personnes}
                scroll={{ y: 250, x: 10 }}
                pagination={{ defaultPageSize: 10, position: ['bottomRight'] }}
              />
            </Spin>
          )}
        </Box>
      ),
    },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>Personnes couvertes - Safe Care</b>
      </Typography>
      <Spin spinning={loading} size="large" style={{ color: '#116599' }}>
        <Form
          form={form}
          autoComplete="off"
          labelAlign="left"
          layout="vertical"
          onFinish={handleEnregistrer}
        >
          {/* Champs cachés */}
          <Form.Item name="IdPersonneCouverte" hidden>
            <InputForm type="text" name="IdPersonneCouverte" notRequired />
          </Form.Item>
          <Form.Item name="IdSouscription" hidden>
            <InputForm type="text" name="IdSouscription" notRequired />
          </Form.Item>
          <Form.Item name="Numcli" hidden>
            <InputForm type="text" name="Numcli" notRequired />
          </Form.Item>

          <div className="clientP">
            <div>
              {/* Barre d'actions */}
              <div className="bG">
                <legend>
                  <Typography color="#116599">Actions</Typography>
                </legend>
                <Form.Item shouldUpdate className="bouttong">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Button
                      size="large"
                      className="bt_add"
                      disabled={consulActive}
                      onClick={handleConsulter}
                      type="ghost"
                      htmlType="button"
                      block
                    >
                      Consulter
                    </Button>
                    <Button
                      size="large"
                      className="bt_add"
                      disabled={ajoutActve}
                      onClick={handleAjouter}
                      type="ghost"
                      htmlType="button"
                      block
                    >
                      Ajouter
                    </Button>
                    <Button
                      size="large"
                      className="bt_add"
                      disabled={enregActve}
                      type="ghost"
                      htmlType="submit"
                      block
                    >
                      Enregistrer
                    </Button>
                    <Button
                      size="large"
                      className="bt_add"
                      disabled={supActive}
                      onClick={handleSupprimer}
                      type="ghost"
                      htmlType="button"
                      block
                    >
                      Supprimer
                    </Button>
                    <Button
                      size="large"
                      className="bt_add"
                      disabled={actuActive}
                      onClick={handleActualiser}
                      type="ghost"
                      htmlType="button"
                      block
                    >
                      Actualiser
                    </Button>
                    <Button
                      size="large"
                      className="bt_add"
                      disabled={annulActive}
                      onClick={handleAnnuler}
                      type="ghost"
                      htmlType="button"
                      block
                    >
                      Annuler
                    </Button>
                  </Space>
                </Form.Item>
              </div>

              {/* Onglets */}
              <Tabs
                size="small"
                type="card"
                activeKey={activeTab}
                onChange={setActiveTab}
                defaultActiveKey="1"
                items={items}
              />

              {/* Formulaire d'ajout de personne couverte */}
              <Box
                display="flex"
                fontStyle="inherit"
                className="border-left tabElement"
                sx={{ p: 2, my: 5 }}
              >
                <div style={{ width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Formulaire personne couverte
                  </Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                    <InputForm
                      label="Nom"
                      name="NomBeneficiaire"
                      type="text"
                      width={6}
                      widthl={2}
                      placeholder="Nom du bénéficiaire"
                    />
                    <InputForm
                      label="Prénom"
                      name="PrenomBeneficiaire"
                      type="text"
                      width={6}
                      widthl={2}
                      placeholder="Prénom du bénéficiaire"
                    />
                    <InputForm
                      label="Date de naissance"
                      name="DateNais"
                      type="date"
                      width={6}
                      widthl={2}
                      placeholder="Date de naissance"
                    />
                    <InputForm
                      label="Lien de parenté"
                      name="LienParente"
                      type="text"
                      width={6}
                      widthl={2}
                      placeholder="Ex: CONJOINT, ENFANT, PARENT"
                    />
                    <InputForm
                      label="N° Relation"
                      name="NumRel"
                      type="text"
                      width={6}
                      widthl={2}
                      placeholder="Numéro de relation (si existant)"
                      notRequired
                    />
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default PersonnesCouvertesPage;
